import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import MasterTypeChip from 'components/chip/MasterTypeChip';
import DetailMetaHeader from 'components/condition-list/DetailMetaHeader';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import ReadOnlyField from 'components/form/ReadOnlyField';
import TextArea from 'components/form/TextArea';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { ClientResult, updateClient } from 'stores/ClientStoreAdapter';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type ClientUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type ClientUpdateFormProps = {
  client: ClientResult;
  clientTypes: MasterTypeResult[];
  isEditing: boolean;
  onClientUpdated: (updatedClient: ClientResult) => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const ClientUpdateForm = React.forwardRef<ClientUpdateFormHandle, ClientUpdateFormProps>(
  (
    {
      client,
      clientTypes,
      isEditing,
      onClientUpdated,
      onClose,
      onEditingChange,
      setProgress,
      hideHeader = false,
    },
    ref,
  ) => {
    const [clientName, setClientName] = useState(client.clientName);
    const [clientNameKana, setClientNameKana] = useState(client.clientNameKana);
    const [clientTypeId, setClientTypeId] = useState(client.clientTypeId);
    const [corporateNumber, setCorporateNumber] = useState(client.corporateNumber || '');
    const [invoiceNumber, setInvoiceNumber] = useState(client.invoiceNumber || '');
    const [address, setAddress] = useState(client.address || '');
    const [url, setUrl] = useState(client.url || '');
    const [contactName, setContactName] = useState(client.contactName || '');
    const [phoneNumber, setPhoneNumber] = useState(client.phoneNumber || '');
    const [mailAddress, setMailAddress] = useState(client.mailAddress || '');
    const [contractStartDate, setContractStartDate] = useState(client.contractStartDate || '');
    const [contractEndDate, setContractEndDate] = useState(client.contractEndDate || '');
    const [remarks, setRemarks] = useState(client.remarks || '');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [copyTooltip, setCopyTooltip] = useState('Copy to clipboard');

    useEffect(() => {
      setClientName(client.clientName);
      setClientNameKana(client.clientNameKana);
      setClientTypeId(client.clientTypeId);
      setCorporateNumber(client.corporateNumber || '');
      setInvoiceNumber(client.invoiceNumber || '');
      setAddress(client.address || '');
      setUrl(client.url || '');
      setContactName(client.contactName || '');
      setPhoneNumber(client.phoneNumber || '');
      setMailAddress(client.mailAddress || '');
      setContractStartDate(client.contractStartDate || '');
      setContractEndDate(client.contractEndDate || '');
      setRemarks(client.remarks || '');
      setErrors({});
      onEditingChange(false);
    }, [client]);

    const handleCopyId = () => {
      navigator.clipboard.writeText(client.clientId).then(() => {
        setCopyTooltip('Copied!');
        setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
      });
    };

    const handleBackToReadOnly = () => {
      setClientName(client.clientName);
      setClientNameKana(client.clientNameKana);
      setClientTypeId(client.clientTypeId);
      setCorporateNumber(client.corporateNumber || '');
      setInvoiceNumber(client.invoiceNumber || '');
      setAddress(client.address || '');
      setUrl(client.url || '');
      setContactName(client.contactName || '');
      setPhoneNumber(client.phoneNumber || '');
      setMailAddress(client.mailAddress || '');
      setContractStartDate(client.contractStartDate || '');
      setContractEndDate(client.contractEndDate || '');
      setRemarks(client.remarks || '');
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!clientName) newErrors.clientName = MSG_REQUIRED('取引先名');
      else if (clientName.length > 300) newErrors.clientName = MSG_MAX_LENGTH('取引先名', 300);

      if (!clientNameKana) newErrors.clientNameKana = MSG_REQUIRED('取引先名（カナ）');
      else if (clientNameKana.length > 300)
        newErrors.clientNameKana = MSG_MAX_LENGTH('取引先名（カナ）', 300);

      if (!clientTypeId) newErrors.clientTypeId = MSG_REQUIRED('取引先区分');

      if (!contractStartDate) newErrors.contractStartDate = MSG_REQUIRED('取引開始日');
      else if (!DATETIME_REGEX.test(contractStartDate))
        newErrors.contractStartDate = MSG_INVALID_DATETIME_FORMAT('取引開始日');

      if (contractEndDate && !DATETIME_REGEX.test(contractEndDate))
        newErrors.contractEndDate = MSG_INVALID_DATETIME_FORMAT('取引終了日');

      if (corporateNumber && corporateNumber.length > 20)
        newErrors.corporateNumber = MSG_MAX_LENGTH('法人番号', 20);

      if (invoiceNumber && invoiceNumber.length > 20)
        newErrors.invoiceNumber = MSG_MAX_LENGTH('インボイス番号', 20);

      if (address && address.length > 500) newErrors.address = MSG_MAX_LENGTH('住所', 500);

      if (url && url.length > 500) newErrors.url = MSG_MAX_LENGTH('企業ホームページ', 500);

      if (contactName && contactName.length > 300)
        newErrors.contactName = MSG_MAX_LENGTH('担当者名', 300);

      if (phoneNumber && phoneNumber.length > 20)
        newErrors.phoneNumber = MSG_MAX_LENGTH('電話番号', 20);

      if (mailAddress && mailAddress.length > 300)
        newErrors.mailAddress = MSG_MAX_LENGTH('メールアドレス', 300);

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateClient({
        clientId: client.clientId,
        clientName,
        clientNameKana,
        clientTypeId,
        ...(corporateNumber && { corporateNumber }),
        ...(invoiceNumber && { invoiceNumber }),
        ...(address && { address }),
        ...(url && { url }),
        ...(contactName && { contactName }),
        ...(phoneNumber && { phoneNumber }),
        ...(mailAddress && { mailAddress }),
        contractStartDate,
        ...(contractEndDate && { contractEndDate }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('取引先情報を更新しました');
          const updatedClient: ClientResult = {
            ...client,
            clientName,
            clientNameKana,
            clientTypeId,
            corporateNumber: corporateNumber || undefined,
            invoiceNumber: invoiceNumber || undefined,
            address: address || undefined,
            url: url || undefined,
            contactName: contactName || undefined,
            phoneNumber: phoneNumber || undefined,
            mailAddress: mailAddress || undefined,
            contractStartDate,
            contractEndDate: contractEndDate || undefined,
            remarks: remarks || undefined,
          };
          onClientUpdated(updatedClient);
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('取引先情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleSave, isEditing: () => isEditing }));

    const getClientTypeLabel = (typeId: string): string => {
      const found = clientTypes.find((t) => t.typeCode === typeId);
      return found ? found.typeLabel : typeId;
    };

    const isContractActive =
      !client.contractEndDate || new Date(client.contractEndDate) >= new Date();

    const clientTypeIndex = clientTypes.findIndex((t) => t.typeCode === client.clientTypeId);

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            {clientTypeIndex >= 0 && (
              <MasterTypeChip
                label={clientTypes[clientTypeIndex].typeLabel}
                colorIndex={clientTypeIndex}
              />
            )}
            <Chip
              label={isContractActive ? '取引中' : '取引終了'}
              size="small"
              color={isContractActive ? 'primary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {client.clientId}
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            {subtitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            作成者: {client.createdByName} 作成日: {formatDate(client.createdAt)}
          </Typography>
        </Stack>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!hideHeader && renderHeader('取引先詳細', '取引先情報を確認します', onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
            {hideHeader && (
              <DetailMetaHeader
                id={client.clientId}
                metaItems={[
                  {
                    label: '作成者',
                    value: `${client.createdByName} (${formatDate(client.createdAt)})`,
                  },
                ]}
              />
            )}
            {/* 基本情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                基本情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="取引先名" value={client.clientName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="取引先名（カナ）" value={client.clientNameKana} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="取引先区分"
                    value={getClientTypeLabel(client.clientTypeId)}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 取引期間 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                取引期間
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField
                    label="取引開始日"
                    value={client.contractStartDate ? formatDate(client.contractStartDate) : ''}
                  />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="取引終了日"
                    value={client.contractEndDate ? formatDate(client.contractEndDate) : ''}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 法人情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                法人情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="法人番号" value={client.corporateNumber || ''} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="インボイス番号" value={client.invoiceNumber || ''} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="住所" value={client.address || ''} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="企業ホームページ" value={client.url || ''} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 連絡先 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                連絡先
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="担当者名" value={client.contactName || ''} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="電話番号" value={client.phoneNumber || ''} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="メールアドレス" value={client.mailAddress || ''} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 備考 */}
            <ReadOnlyField label="備考" value={client.remarks || ''} />
          </Stack>
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader &&
          renderHeader('取引先詳細（編集中）', '取引先情報を編集します', handleBackToReadOnly)}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {hideHeader && (
            <DetailMetaHeader
              id={client.clientId}
              metaItems={[
                {
                  label: '作成者',
                  value: `${client.createdByName} (${formatDate(client.createdAt)})`,
                },
              ]}
            />
          )}
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="取引先名"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.clientName}
                  helperText={errors.clientName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="取引先名（カナ）"
                  value={clientNameKana}
                  onChange={(v) => setClientNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.clientNameKana}
                  helperText={errors.clientNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControl error={!!errors.clientTypeId} required>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>取引先区分</FormLabel>
                  <RadioGroup
                    row
                    value={clientTypeId}
                    onChange={(e) => setClientTypeId(e.target.value)}
                  >
                    {clientTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                  {errors.clientTypeId && <FormHelperText>{errors.clientTypeId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 取引期間 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              取引期間
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={contractStartDate}
                  onChange={setContractStartDate}
                  label="取引開始日"
                  required
                  error={!!errors.contractStartDate}
                  helperText={errors.contractStartDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value={contractEndDate}
                  onChange={setContractEndDate}
                  label="取引終了日"
                  error={!!errors.contractEndDate}
                  helperText={errors.contractEndDate}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 法人情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              法人情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="法人番号"
                  value={corporateNumber}
                  onChange={(e) => setCorporateNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.corporateNumber}
                  helperText={errors.corporateNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="インボイス番号"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="住所"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="企業ホームページ"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.url}
                  helperText={errors.url}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 連絡先 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              連絡先
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="担当者名"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="電話番号"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メールアドレス"
                  value={mailAddress}
                  onChange={(e) => setMailAddress(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.mailAddress}
                  helperText={errors.mailAddress}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

ClientUpdateForm.displayName = 'ClientUpdateForm';

export default ClientUpdateForm;
